import * as React from "react"
import { Row, Col } from "react-grid-system"
import styled from "styled-components"
import H3 from "../atoms/typography/H3"
import LoadingSpinner from "../atoms/LoadingSpinner"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import { PersonRemoveIcon } from "../atoms/icons/PersonRemoveIcon"
import { AnimatePresence } from "framer-motion"
import Modal from "./Modals/Modal"
import RemoveMemberFromOrgOrGroupModalContent from "./Modals/RemoveMemberFromOrgOrGroupModalContent"
import { Member } from "../../utils/models"
import SelectRoleDropdown from "../atoms/inputs/SelectRoleDropdown"
import { useInviteMembersToGroup } from "../../hooks/useinviteMembersToGroup"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"

interface Props {
  group: any
  canEdit?: boolean
  loading?: boolean
  member?: Member
  role?: string
  onClickRemoveFromGroup: (groupsId: number | string, orgId: number | string) => void
}

const OrgRow = styled(Row)`
  padding: ${(props) => props.theme.spacings.extreme};
  background-color: ${(props) => props.theme.colors.greyLighter};
  margin: ${(props) => props.theme.spacings.large};
`

const NameRoleSection = styled.div`
  display: flex;
  align-items: center;
`

const MemberActionBar: React.FunctionComponent<Props> = ({
  group,
  canEdit,
  loading,
  member,
  role,
  onClickRemoveFromGroup,
}) => {
  const [modalOpen, setModalOpen] = React.useState(false)
  const [selectedRole, setSelectedRole] = React.useState(role)
  const onCompletedSendInviteToMember = () => {
    console.log("Sent invite to member")
  }
  const onErrorSendInviteToMember = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error sending invite to member")
  }
  const { inviteMembersToGroup } = useInviteMembersToGroup({
    onCompleted: onCompletedSendInviteToMember,
    onError: onErrorSendInviteToMember,
  })
  const onHandleRoleChange = async (role: string) => {
    try {
      await inviteMembersToGroup({
        variables: {
          organizationGroupId: group.id,
          organizationId: group.organization?.id ?? "error",
          users: [{ email: member?.member.email, role: role }],
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error sending invite to member")
    }
  }

  return (
    <div>
      <OrgRow justify="between" align="center">
        <Col lg={3}>
          <Row justify="between" align="center">
            <NameRoleSection>
              <H3>{group.name}</H3>
            </NameRoleSection>
            {canEdit && selectedRole && (
              <SelectRoleDropdown
                title={""}
                defaultValue={selectedRole}
                onChange={(role) => onHandleRoleChange(role)}
              />
            )}
          </Row>
        </Col>
        {canEdit && (
          <div>
            {loading ? (
              <LoadingSpinner />
            ) : (
              <PrimaryButton
                title={`Remove from ${group.name}`}
                icon={<PersonRemoveIcon />}
                onClick={() => setModalOpen(true)}
              />
            )}
          </div>
        )}
      </OrgRow>
      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {modalOpen && (
          <Modal open={modalOpen} onClose={() => setModalOpen(!modalOpen)} closeIcon>
            <RemoveMemberFromOrgOrGroupModalContent
              memberName={member?.member.name ?? ""}
              onClose={() => setModalOpen(!modalOpen)}
              onClickDelete={() => onClickRemoveFromGroup(group.id, group.organization?.id ?? "error")}
            />
          </Modal>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MemberActionBar
